import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Loader2, FileText, Eye, Star, Gavel, CheckCircle2, Send, MailCheck, Plus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { recruitmentClientApi } from '@/lib/recruitmentApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { RecruitmentStepper } from '@/components/recruitment/RecruitmentStepper';
import { IntakeFormDialog } from '@/components/recruitment/IntakeFormDialog';
import { ClientSelfServiceOps } from '@/components/recruitment/ClientSelfServiceOps';
import { ClientTalentCheckCreateDialog } from '@/components/client/ClientTalentCheckCreateDialog';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';
import { CandidateRosterCard } from '@/components/recruitment/CandidateRosterCard';
import { CandidateInbox } from '@/components/recruitment/CandidateInbox';
import ActivityTimelinePanel from '@/components/recruitment/ActivityTimelinePanel';
import { VacancyRequestsClient } from '@/components/recruitment/VacancyRequestsClient';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import i18n from 'i18next';
import { bizDate } from '@/lib/dates';

function fmtDate(iso) {
  return bizDate(iso, i18n.language);
}

// Workflow Improvement #001 — map a vacancy request into intake-form defaults
// so clients never re-type information they already provided.
const INTAKE_EDU = ['high_school', 'bachelor', 'master', 'phd'];
const INTAKE_URG = { urgent: 'critical', within_30_days: 'high', within_60_days: 'medium', no_timeline: 'low' };
const INTAKE_LANG = {
  'español': 'spanish', espanol: 'spanish', spanish: 'spanish',
  'inglés': 'english', ingles: 'english', english: 'english',
  'francés': 'french', frances: 'french', french: 'french',
  'portugués': 'portuguese', portugues: 'portuguese', portuguese: 'portuguese',
};
function intakePrefillFromVacancy(v) {
  if (!v) return null;
  const loc = (v.work_location || '').toLowerCase();
  return {
    position_title: v.position_title || '',
    department: v.department || '',
    number_of_openings: v.openings || 1,
    years_experience: v.years_experience ?? '',
    education_level: INTAKE_EDU.includes(v.education_level)
      ? v.education_level : (v.education_level ? 'other' : 'bachelor'),
    salary_range: v.budget_defined && v.budget_from != null ? `${v.budget_from} - ${v.budget_to}` : '',
    urgency: INTAKE_URG[v.hiring_priority] || 'medium',
    languages: [...new Set((v.languages || []).map((l) => INTAKE_LANG[(l.language || '').trim().toLowerCase()] || 'other'))],
    key_responsibilities: v.competencies_text || v.technical_knowledge || '',
    work_modality: loc.includes('remot') ? 'remote' : 'hybrid',
  };
}

function ClientRecruitmentList({ rows }) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <div className="py-16 text-sm text-muted-foreground text-center" data-testid="recruitment-client-empty">
        {t('recruitment.emptyClient')}
      </div>
    );
  }
  return (
    <Table data-testid="recruitment-client-table">
      <TableHeader className="bg-muted/40">
        <TableRow>
          <TableHead>{t('recruitment.thPosition')}</TableHead>
          <TableHead>{t('recruitment.thStage')}</TableHead>
          <TableHead className="hidden md:table-cell">{t('recruitment.thOpened')}</TableHead>
          <TableHead className="text-right">{t('recruitment.thAction')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`recruitment-client-row-${r.id}`}>
            <TableCell className="font-medium">
              <p>{r.position_title}</p>
              {r.department && <p className="text-xs text-muted-foreground">{r.department}</p>}
            </TableCell>
            <TableCell>
              <Badge variant="secondary">
                {r.stage}/11 — {t(`recruitment.stages.${r.stage}`)}
              </Badge>
            </TableCell>
            <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
            <TableCell className="text-right">
              <Link to={`/client/recruitment/${r.id}`}>
                <Button size="sm" variant="outline" className="gap-1.5">
                  <Eye className="h-3.5 w-3.5" /> {t('recruitment.openCta')}
                </Button>
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const OUTCOME_KEYS = ['selected', 'not_selected', 'on_hold', 'cancelled'];
const OUTCOME_DEFAULTS = {
  selected: 'Selected / Promoted', not_selected: 'Not Selected',
  on_hold: 'On Hold', cancelled: 'Cancelled',
};

function CandidateNotifyBlock({ proc, onSaved }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const outcome = proc.business_outcome;
  const candidateName = proc.candidate_snapshot?.name || t('recruitment.notify.candidateFallback', { defaultValue: 'the candidate' });
  const notified = proc.candidate_notification && proc.candidate_notification.outcome === outcome;

  if (!outcome) return null;

  if (notified) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-xs" data-testid="candidate-notified-banner">
        <MailCheck className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
        <span className="leading-relaxed">
          {t('recruitment.notify.sentOn', { date: fmtDate(proc.candidate_notification.sent_at) })}
        </span>
      </div>
    );
  }

  const openDialog = () => {
    setMessage(t(`recruitment.notify.templates.${outcome}`, {
      name: candidateName,
      position: proc.position_title || '—',
      company: proc.company_name || '',
    }));
    setOpen(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const fresh = await api.post(`/client/recruitment-processes/${proc.id}/notify-candidate`, { message: message.trim() });
      toast.success(t('recruitment.notify.sentToast'));
      setOpen(false);
      onSaved?.(fresh?.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5" data-testid="candidate-notify-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground leading-relaxed flex-1 min-w-[200px]">
          {t('recruitment.notify.hint', { name: candidateName })}
        </p>
        <Button size="sm" variant="outline" onClick={openDialog} className="gap-1.5" data-testid="notify-candidate-cta">
          <Send className="h-3.5 w-3.5" /> {t('recruitment.notify.cta')}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="notify-candidate-dialog">
          <DialogHeader>
            <DialogTitle>{t('recruitment.notify.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('recruitment.notify.dialogHint')}</DialogDescription>
          </DialogHeader>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{t('recruitment.notify.messageLabel')}</p>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              className="min-h-[220px] text-sm" maxLength={4000}
              data-testid="notify-candidate-message"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending} data-testid="notify-candidate-skip">
              {t('recruitment.notify.skip')}
            </Button>
            <Button onClick={send} disabled={sending || message.trim().length < 10} className="gap-1.5" data-testid="notify-candidate-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t('recruitment.notify.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HiringDecisionCard({ proc, onSaved }) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState(proc.business_outcome || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [roster, setRoster] = useState([]);
  const [selUid, setSelUid] = useState('');
  const closed = proc.stage >= 11;

  useEffect(() => {
    if (outcome !== 'selected' || closed) return;
    api.get(`/client/recruitment-processes/${proc.id}/candidates`)
      .then((r) => setRoster(Array.isArray(r.data) ? r.data.filter((c) => c.candidate_user_id) : []))
      .catch(() => setRoster([]));
  }, [outcome, closed, proc.id]);

  const save = async () => {
    if (!outcome) return;
    setSaving(true);
    try {
      await api.post(`/client/recruitment-processes/${proc.id}/business-outcome`, {
        outcome, notes: notes.trim() || null,
        selected_candidate_user_id: outcome === 'selected' ? (selUid || null) : null,
      });
      toast.success(t('recruitment.decision.savedToast', { defaultValue: 'Hiring decision recorded' }));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (closed) {
    return (
      <Card className="p-5 shadow-card border-emerald-200/60 bg-emerald-50/30" data-testid="hiring-decision-closed">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h3 className="font-semibold">{t('recruitment.decision.closedTitle', { defaultValue: 'Process closed' })}</h3>
        </div>
        {proc.business_outcome && (
          <div className="text-sm mt-2 flex items-center flex-wrap gap-1">
            <span>{t('recruitment.decision.finalOutcome', { defaultValue: 'Final outcome' })}:</span>
            <Badge variant="outline" className="ml-1" data-testid="hiring-decision-outcome">
              {t(`recruitment.decision.outcome.${proc.business_outcome}`, { defaultValue: OUTCOME_DEFAULTS[proc.business_outcome] || proc.business_outcome })}
            </Badge>
          </div>
        )}
        {proc.business_outcome_notes && (
          <p className="text-xs text-muted-foreground mt-2">{proc.business_outcome_notes}</p>
        )}
        <CandidateNotifyBlock proc={proc} onSaved={onSaved} />
      </Card>
    );
  }

  return (
    <Card className="p-5 shadow-card border-primary/20" data-testid="hiring-decision-card">
      <div className="flex items-center gap-2 mb-1">
        <Gavel className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">{t('recruitment.decision.title', { defaultValue: 'Record hiring decision' })}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        {proc.business_outcome === 'on_hold'
          ? t('recruitment.decision.onHoldHint', { defaultValue: 'This process is On Hold — you can update the decision at any time. A final outcome will close the process.' })
          : t('recruitment.decision.hint', { defaultValue: 'Assessment results are available. Record the final outcome to close the process, or mark it On Hold to decide later.' })}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          {OUTCOME_KEYS.map((k) => (
            <label key={k} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${outcome === k ? 'border-primary bg-primary/5 font-medium' : 'border-border/60 hover:bg-muted/40'}`}>
              <input type="radio" name="hiring-outcome" value={k} checked={outcome === k}
                     onChange={() => setOutcome(k)} data-testid={`hiring-outcome-${k}`} />
              {t(`recruitment.decision.outcome.${k}`, { defaultValue: OUTCOME_DEFAULTS[k] })}
              {k === 'on_hold' && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {t('recruitment.decision.keepsOpen', { defaultValue: 'keeps the process open' })}
                </span>
              )}
            </label>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          {outcome === 'selected' && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5 space-y-1.5" data-testid="selected-candidate-block">
              <p className="text-xs font-medium text-emerald-900">
                {t('recruitment.decision.whoSelected', { defaultValue: '¿Qué candidato fue seleccionado?' })}
              </p>
              <select className="w-full rounded-md border border-border/60 bg-background p-2 text-sm"
                      value={selUid} onChange={(e) => setSelUid(e.target.value)}
                      data-testid="selected-candidate-select">
                <option value="">{t('recruitment.decision.pickCandidate', { defaultValue: 'Selecciona al candidato…' })}</option>
                {roster.map((c) => (
                  <option key={c.candidate_user_id} value={c.candidate_user_id}>{c.candidate_name}</option>
                ))}
              </select>
              <p className="text-[11px] text-emerald-800">
                {t('recruitment.decision.whoSelectedHint', { defaultValue: 'Queda registrado como contratado y podrás activarlo como colaborador.' })}
              </p>
            </div>
          )}
          <textarea
            className="flex-1 min-h-[90px] rounded-md border border-border/60 bg-background p-3 text-sm resize-none"
            placeholder={t('recruitment.decision.notesPh', { defaultValue: 'Optional notes (visible to CTH)' })}
            value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000}
            data-testid="hiring-decision-notes"
          />
          <Button onClick={save} disabled={!outcome || saving} data-testid="hiring-decision-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {outcome && outcome !== 'on_hold'
              ? t('recruitment.decision.submitFinal', { defaultValue: 'Record decision & close process' })
              : t('recruitment.decision.submitHold', { defaultValue: 'Save decision' })}
          </Button>
        </div>
      </div>
      {proc.business_outcome === 'on_hold' && <CandidateNotifyBlock proc={proc} onSaved={onSaved} />}
    </Card>
  );
}

function ClientRecruitmentDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [proc, setProc] = useState(null);
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intakeOpen, setIntakeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProc(await recruitmentClientApi.get(id));
    } catch (e) {
      toast.error(t('recruitment.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!proc?.vacancy_request_id) { setVacancy(null); return; }
    api.get(`/client/vacancy-requests/${proc.vacancy_request_id}`)
      .then((r) => setVacancy(r.data))
      .catch(() => setVacancy(null));
  }, [proc?.vacancy_request_id]);

  if (loading || !proc) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
      </div>
    );
  }

  const showIntakeCta = proc.stage === 1 && !proc.intake_form;

  return (
    <>
      <Topbar
        title={proc.position_title}
        subtitle={`${proc.company_name || ''}${proc.department ? ' · ' + proc.department : ''}`}
        actions={
          <Link to="/client/recruitment">
            <Button size="sm" variant="outline" className="gap-1.5">
              <ArrowLeft className="h-4 w-4" /> {t('common.back')}
            </Button>
          </Link>
        }
      />
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-5 shadow-card">
          <RecruitmentStepper currentStage={proc.stage} stageHistory={proc.stage_history} />
          {/* Founder 2026-06 — informational structural-linkage status. */}
          <p className="text-xs text-muted-foreground mt-2" data-testid="proc-formal-position-status">
            {proc.formal_position_id
              ? t('recruitment.formalLinked', { title: proc.formal_position_title, defaultValue: 'Vinculado al puesto formal: {{title}}' })
              : t('recruitment.formalUnlinked', { defaultValue: 'Sin puesto formal vinculado' })}
          </p>
        </Card>

        {/* Split model (founder-approved) — the client records the hiring
             decision once automatic results exist (stage >= 6). The CTH
             interpretive report continues in parallel. */}
        {proc.stage >= 6 && (
          <HiringDecisionCard proc={proc} onSaved={load} />
        )}

        {showIntakeCta && (
          <Card className="p-5 shadow-card border-warning/40 bg-warning/5" data-testid="intake-pending-card">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-warning mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold">{t('recruitment.intake.pendingTitle')}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t('recruitment.intake.pendingDesc')}</p>
              </div>
              <Button
                data-testid="btn-fill-intake"
                className="bg-gradient-primary hover:opacity-90 gap-1.5"
                onClick={() => setIntakeOpen(true)}
              >
                <FileText className="h-4 w-4" /> {t('recruitment.intake.fillCta')}
              </Button>
            </div>
          </Card>
        )}

        {proc.intake_form && (
          <Card className="p-5 shadow-card" data-testid="client-intake-snapshot">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">{t('recruitment.intake.title')}</h3>
              <Badge variant="outline" className="text-xs">{t('recruitment.intake.submittedTag')}</Badge>
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t('recruitment.intake.fields.positionTitle')}</dt>
              <dd className="font-medium">{proc.intake_form.position_title}</dd>
              <dt className="text-muted-foreground">{t('recruitment.intake.fields.department')}</dt>
              <dd className="font-medium">{proc.intake_form.department}</dd>
              <dt className="text-muted-foreground">{t('recruitment.intake.fields.urgency')}</dt>
              <dd className="font-medium">{t(`recruitment.intake.urgencyOpts.${proc.intake_form.urgency || 'medium'}`)}</dd>
              <dt className="text-muted-foreground">{t('recruitment.intake.fields.numberOfOpenings')}</dt>
              <dd className="font-medium">{proc.intake_form.number_of_openings}</dd>
            </dl>
          </Card>
        )}

        {/* Sprint TC-D.2 — Shortlist summary (client-read-only) */}
        <ClientShortlistCard processId={proc.id} shortlistedIds={proc.shortlisted_candidate_ids || []} />

        {/* Sprint A · G-1 — Named candidate roster (sanitized client variant) */}
        <CandidateRosterCard
          processId={proc.id}
          variant="client"
          canManage={proc.stage >= 3 && !proc.closed_at && proc.stage < 11}
          canActivate={proc.stage >= 3}
          onProcessChanged={load}
        />

        {/* Client Autonomy P0 — client-owned process notes */}
        {(proc.client_notes || []).length > 0 && (
          <Card className="p-5 shadow-card" data-testid="client-process-notes">
            <h3 className="font-semibold mb-2">{t('recruitment.clientNotes', { defaultValue: i18n.language?.startsWith('es') ? 'Notas del proceso' : 'Process notes' })}</h3>
            <ul className="space-y-2">
              {proc.client_notes.map((n) => (
                <li key={n.id} className="text-sm rounded-md border border-border bg-muted/30 px-3 py-2">
                  <p>{n.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.author_name} · {fmtDate(n.created_at)}</p>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Client Autonomy MVA — self-service process management */}
        <ClientSelfServiceOps proc={proc} onChanged={load} />

        {/* Sprint TC-D.2 — Client-safe activity timeline */}
        <ActivityTimelinePanel
          processId={proc.id}
          fetcher={recruitmentClientApi.getActivity}
          viewerRole="client"
        />

        <IntakeFormDialog
          open={intakeOpen}
          onOpenChange={setIntakeOpen}
          processId={proc.id}
          defaultPosition={proc.position_title}
          defaultDepartment={proc.department}
          prefill={intakePrefillFromVacancy(vacancy)}
          onSubmitted={load}
        />
      </div>
    </>
  );
}

function ClientShortlistCard({ processId, shortlistedIds }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await recruitmentClientApi.listCandidates(processId);
        if (alive) {
          setRows((all || []).filter((r) => r.shortlisted));
        }
      } catch (e) {
        // Best-effort — the roster card below also surfaces errors.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [processId, shortlistedIds.join(',')]);

  return (
    <Card className="p-5 shadow-card" data-testid="client-shortlist-card">
      <div className="flex items-center gap-2 mb-3">
        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
        <h3 className="font-semibold">
          {t('clientTalentCheck.shortlist.title', { defaultValue: 'Shortlisted candidates' })}
        </h3>
        <Badge variant="outline" className="ml-auto text-xs tabular-nums" data-testid="client-shortlist-count">
          {rows.length}
        </Badge>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground py-2">
          {t('common.loading')}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="client-shortlist-empty">
          {t('clientTalentCheck.shortlist.empty', {
            defaultValue: 'Corporate Talent Hub has not shortlisted any candidates for this position yet.',
          })}
        </p>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="client-shortlist-list">
          {rows.map((r) => (
            <li
              key={r.assignment_id}
              className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2"
              data-testid={`client-shortlist-row-${r.assignment_id}`}
            >
              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.candidate_name}</p>
                {r.report_released && (
                  <p className="text-xs text-success">
                    {t('clientTalentCheck.shortlist.reportReleased', { defaultValue: 'Report released' })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function NewProcessSelfService({ onCreated }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="tc-new-process-btn" className="gap-1.5">
        <Plus className="h-4 w-4" /> {L('Crear proceso', 'Create process')}
      </Button>
      <ClientTalentCheckCreateDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => { setOpen(false); onCreated && onCreated(); }}
      />
    </>
  );
}

export default function ClientRecruitment() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'vacancies' ? 'vacancies' : 'processes';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await recruitmentClientApi.list());
    } catch (e) {
      toast.error(t('recruitment.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { if (!id) load(); }, [load, id]);

  if (id) return <ClientRecruitmentDetail />;

  return (
    <>
      <Topbar
        title={t('recruitment.title')}
        subtitle={t('recruitment.subtitle')}
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <NewProcessSelfService onCreated={load} />
          <RequestHMSupport suite="talentcheck" origin="/client/recruitment" />
        </div>
        <Tabs value={tab} onValueChange={(v) => setSearchParams(v === 'vacancies' ? { tab: 'vacancies' } : {})}>
          <TabsList>
            <TabsTrigger value="processes" data-testid="recruitment-tab-processes">
              {t('vacancy.tabProcesses', { defaultValue: 'Procesos' })}
            </TabsTrigger>
            <TabsTrigger value="vacancies" data-testid="recruitment-tab-vacancies">
              {t('vacancy.tabVacancies', { defaultValue: 'Vacantes' })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'vacancies' ? (
          <VacancyRequestsClient />
        ) : (
          <>
            {!loading && <CandidateInbox processes={rows} />}
            <Card className="shadow-card overflow-hidden">
              {loading ? (
                <div className="py-16 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
                </div>
              ) : (
                <ClientRecruitmentList rows={rows} />
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}
